import moment from "moment";
const SAMPLING_END_DATE = moment.utc("2025-01-31T23:59:59");
const CONTROL_GROUP_RATE = 0.5;

/**
 * Returns true if the experiment is open for new workspaces to join
 *
 * @returns {boolean}
 */
function open() {
    return moment.utc().isBefore(SAMPLING_END_DATE);
}

/**
 * Returns the ISO string of the end date of the experiment if the workspace joined the experiment.
 *
 * @param {object} params
 * @param {import('@functions/state/types').WorkspaceUsage} params.usage
 * @returns {string | undefined}
 */
function value({ usage }) {
    return usage?.features?.freeTrainGpu;
}

/**
 * Returns the readable value of the experiment (short text like "1d 2h left")
 *
 * @param {object} params
 * @param {import('@functions/state/types').WorkspaceUsage} params.usage
 * @returns {string}
 */
function shortReadableValue({ usage }) {
    const endDateIsoString = value({ usage });
    if (!endDateIsoString) return "";

    if (moment.utc().isBefore(endDateIsoString)) {
        const days = Math.abs(moment(endDateIsoString).diff(moment.utc(), "days"));
        const hours = Math.abs(moment(endDateIsoString).diff(moment.utc(), "hours")) % 24;
        return `${days}d ${hours}h left`;
    } else {
        const days = Math.abs(moment(endDateIsoString).diff(moment.utc(), "days"));
        const hours = Math.abs(moment(endDateIsoString).diff(moment.utc(), "hours")) % 24;
        return `${days}d ${hours}h ago`;
    }
}

/**
 * Returns the readable value of the experiment (long text like "Ends in 1 day and 2 hours")
 *
 * @param {object} params
 * @param {import('@functions/state/types').WorkspaceUsage} params.usage
 * @returns {string}
 */
function readableValue({ usage }) {
    const endDateIsoString = value({ usage });
    if (!endDateIsoString) return "";

    const days = Math.abs(moment(endDateIsoString).diff(moment.utc(), "days"));
    const hours = Math.abs(moment(endDateIsoString).diff(moment.utc(), "hours")) % 24;

    if (moment.utc().isBefore(endDateIsoString)) {
        if (days > 0) {
            return `Ends in ${days} day${days === 1 ? "" : "s"} and ${hours} hour${hours === 1 ? "" : "s"}`;
        }

        if (hours > 0) {
            return `Ends in ${hours} hour${hours === 1 ? "" : "s"}`;
        }

        const minutes = moment(endDateIsoString).diff(moment.utc(), "minutes") % 60;

        return `Ends in ${minutes} minute${minutes === 1 ? "" : "s"}`;
    } else {
        if (days > 0) {
            return `Ended ${days} day${days === 1 ? "" : "s"} ago`;
        }

        if (hours > 0) {
            return `Ended ${hours} hour${hours === 1 ? "" : "s"} ago`;
        }

        return "Ended just now";
    }
}

/**
 * Returns the default end date of the experiment, starting from now
 *
 * @returns {string}
 */
function defaultEndDate() {
    return moment.utc().add(7, "days").toISOString();
}

/**
 * Returns false if the workspace didn't join the experiment.
 * Returns the ISO string of the end date of the experiment if the workspace joined the experiment.
 *
 * @returns {boolean | string}
 */
function sample() {
    // it's over, workspaces can't join the experiment
    if (!open()) return false;

    const isControlGroup = Math.random() < CONTROL_GROUP_RATE;

    if (isControlGroup) {
        return false;
    }

    return defaultEndDate();
}

/**
 * Returns true if the workspace has access to the free train GPU
 *
 * @param {object} params
 * @param {import('@functions/state/types').WorkspaceUsage} params.usage
 * @param {moment.Moment} params.comparisonDate
 * @returns {boolean}
 */
function enabled({ usage, comparisonDate }) {
    const endDateIsoString = value({ usage });

    if (!endDateIsoString) return false;

    const date = comparisonDate || moment.utc(); // fallback to now if no comparison date is provided
    const endDate = moment.utc(endDateIsoString);

    return date.isSameOrBefore(endDate);
}

export { open, sample, enabled, value, shortReadableValue, readableValue, defaultEndDate };
export default { open, sample, enabled, value, shortReadableValue, readableValue, defaultEndDate };
